/**
 * JobAttach — ӨЭА нарядад БАРИМТ хавсаргах (нэртэйгээр).
 *
 * Зөвшөөрлийн хуудасны 3-р хэсэг «АЖИЛ ГҮЙЦЭТГЭХТЭЙ ХОЛБООТОЙ БАРИМТ БИЧИГ»:
 *   3.1 ХАБ-ын заавар, журмын дугаар  → тоног төхөөрөмжөөс АВТОМАТААР
 *   3.2 Технологийн карт               → тоног төхөөрөмжөөс АВТОМАТААР
 *   3.3 Ажлыг аюулгүй зохион байгуулах ТӨСӨЛ  ← энд файлаар
 *   3.4 Хэмжилт, шинжилгээний ҮР ДҮН          ← энд файлаар
 *
 * 3.3/3.4 нь дугаар биш бодит баримт учир файл + НЭР-ээр хадгална
 * («Тоос хэмжилт 2026-08», «Кран угсрах төсөл v2» гэх мэт).
 *
 * ⚠ Наряд хадгалагдсаны дараа л хавсаргана — файл нь `job_id`-д холбогдоно.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Input, Select, Upload, Button, Tooltip, Tag, message, Popconfirm } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";

import * as API from "src/api/high_risk";
import { guardFile } from "src/components/MediaUpload";

const KINDS = [
  { value: "project", label: "3.3 Аюулгүй зохион байгуулах төсөл" },
  { value: "measure", label: "3.4 Хэмжилт, шинжилгээний үр дүн" },
  { value: "other", label: "Бусад баримт" },
];

const kindLabel = (v) =>
  (KINDS.find((k) => k.value === v) || {}).label || "Бусад баримт";

export default function JobAttach({ jobId, readonly }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("project");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!jobId) return;
    API.listJobAttach(jobId)
      .then(setRows)
      .catch(() => setRows([]));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  // Наряд хадгалагдаагүй бол хавсаргах боломжгүй — job_id байхгүй
  if (!jobId) {
    return (
      <div className="text-[11px] text-gray-400">
        Нарядыг хадгалсны дараа баримт хавсаргах боломжтой болно.
      </div>
    );
  }

  const doUpload = async (file) => {
    if (!String(name).trim()) {
      message.warning("Баримтын нэрийг эхлээд оруулна уу");
      return false;
    }
    // Хэт том файлд анхааруулна (components/MediaUpload.js).
    if (!(await guardFile(file))) return false;
    setBusy(true);
    API.uploadJobAttach(jobId, file, name.trim(), kind)
      .then(() => {
        message.success("Хавсаргалаа");
        setName("");
        load();
      })
      .catch((e) =>
        message.error(e?.response?.data?.message || "Хавсаргаж чадсангүй"),
      )
      .finally(() => setBusy(false));
    return false; // antd Upload өөрөө илгээхгүй
  };

  const doDelete = (row) =>
    API.deleteJobAttach(row.id)
      .then(() => {
        message.success("Устгалаа");
        load();
      })
      .catch((e) =>
        message.error(e?.response?.data?.message || "Устгаж чадсангүй"),
      );

  return (
    <div className="w-full">
      {!readonly && (
        <div className="flex gap-2 items-center mb-2">
          <Select
            className="text-xs"
            style={{ width: 210 }}
            value={kind}
            onChange={setKind}
            options={KINDS}
          />
          <Input
            className="text-xs flex-1"
            placeholder="Баримтын нэр (жш «Тоос хэмжилт 2026-08»)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Upload
            showUploadList={false}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            beforeUpload={doUpload}
          >
            <Button size="small" icon={<UploadOutlined />} loading={busy}>
              Хавсаргах
            </Button>
          </Upload>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-[11px] text-gray-400">Хавсаргасан баримт алга</div>
      ) : (
        <div className="border rounded" style={{ borderColor: "#E6E9EF" }}>
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2 px-2 py-1"
              style={{ borderTop: "1px solid #F1F2F4" }}
            >
              <PaperClipOutlined style={{ color: "#0EA5E9" }} />
              <a
                href={r.file_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold flex-1 truncate"
                title={r.file_name}
              >
                {r.description || r.file_name}
              </a>
              <Tag style={{ margin: 0, fontSize: 10 }}>
                {kindLabel(r.doc_kind)}
              </Tag>
              {r.insert_date ? (
                <span className="text-[10px] text-gray-400">
                  {r.insert_date}
                </span>
              ) : null}
              {!readonly && (
                <Popconfirm
                  title="Хавсралтыг устгах уу?"
                  okText="Устгах"
                  cancelText="Болих"
                  onConfirm={() => doDelete(r)}
                >
                  <Tooltip title="Устгах">
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
